import Routing, { type RouteName } from "routing";

export type Violation = {
  path: string;
  message: string;
};

export type ApiErrorEnvelope = {
  code: number | null;
  message: string | null;
  violations: Violation[];
  details: Record<string, unknown> | null;
};

type ApiErrorOptions = {
  violations?: Violation[];
  details?: Record<string, unknown> | null;
  retryAfter?: number | null;
};

export class ApiError extends Error {
  readonly violations: Violation[];

  readonly details: Record<string, unknown> | null;

  readonly retryAfter: number | null;

  constructor(
    readonly status: number,
    message: string,
    options: ApiErrorOptions = {},
  ) {
    super(message);
    this.name = "ApiError";
    this.violations = options.violations ?? [];
    this.details = options.details ?? null;
    this.retryAfter = options.retryAfter ?? null;
  }
}

type PathParams = Record<string, string | number>;
type QueryParams = Record<string, string | number | boolean | undefined>;

export type ApiRequest = {
  route: RouteName;
  params?: PathParams;
  query?: QueryParams;
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
};

const JSON_HEADERS = { "Content-Type": "application/json" };

export function url(
  route: RouteName,
  params: PathParams = {},
  query: QueryParams = {},
): string {
  const defined: Record<string, string> = {};

  for (const [key, value] of Object.entries(query)) {
    if (undefined !== value) {
      defined[key] = String(value);
    }
  }

  return Routing.generate(route, params, defined);
}

export async function request<T>({
  route,
  params,
  query,
  method = "GET",
  body,
  signal,
}: ApiRequest): Promise<T> {
  const response = await fetch(url(route, params, query), {
    method,
    signal,
    ...(undefined === body
      ? {}
      : { headers: JSON_HEADERS, body: JSON.stringify(body) }),
  });

  if (!response.ok) {
    throw await apiError(response);
  }

  // 204 on a delete: there is nothing to read, and callers type it as void.
  return 204 === response.status ? (undefined as T) : await response.json();
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return "object" === typeof value && null !== value
    ? (value as Record<string, unknown>)
    : null;
}

function asText(value: unknown): string | null {
  return "string" === typeof value && "" !== value ? value : null;
}

function violationsOf(envelope: Record<string, unknown> | null): Violation[] {
  const rows = envelope?.violations;

  if (!Array.isArray(rows)) {
    return [];
  }

  return rows.flatMap((row) => {
    const violation = asRecord(row);

    return violation && "string" === typeof violation.path
      ? [{ path: violation.path, message: String(violation.message ?? "") }]
      : [];
  });
}

function envelopeOf(payload: Record<string, unknown> | null): ApiErrorEnvelope {
  const error = asRecord(payload?.error);

  return {
    code: "number" === typeof error?.code ? error.code : null,
    message: asText(error?.message),
    violations: violationsOf(error),
    details: asRecord(error?.details),
  };
}

function retryAfterOf(response: Response): number | null {
  const header = response.headers.get("Retry-After")?.trim();

  if (undefined === header || "" === header) {
    return null;
  }

  const seconds = Number(header);

  if (Number.isFinite(seconds)) {
    return Math.max(0, Math.trunc(seconds));
  }

  const date = Date.parse(header);

  return Number.isNaN(date)
    ? null
    : Math.max(0, Math.round((date - Date.now()) / 1000));
}

async function apiError(response: Response): Promise<ApiError> {
  const payload = asRecord(await response.json().catch(() => null));
  const envelope = envelopeOf(payload);

  const message =
    envelope.message ??
    asText(payload?.detail) ??
    asText(payload?.message) ??
    asText(payload?.title) ??
    asText(response.statusText) ??
    `HTTP ${response.status}`;

  return new ApiError(response.status, message, {
    violations: envelope.violations,
    details: envelope.details,
    retryAfter: retryAfterOf(response),
  });
}
